import React, { useState, useEffect, useRef, Suspense } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import LandingPage from './landing-page/LandingPage';
import RouteErrorBoundary from './components/RouteErrorBoundary';
import lazyWithRetry from './utils/lazyWithRetry';
import CustomCursor from './components/CustomCursor';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Lenis from 'lenis';
import CartOverlay from './components/CartOverlay';
import CookieBanner from './components/CookieBanner';
import {
  SEO_SUBPAGE_ROUTES,
  MAIN_TAB_ROUTES as BASE_MAIN_TAB_ROUTES,
  LEGACY_ALIASES,
  HOME_HREFLANG,
  PAGE_META,
} from './seo/routes';

// Lazy loaded components for code splitting
const Impressum = lazyWithRetry(() => import('./components/Impressum'));
const Cre8AppsPage = lazyWithRetry(() => import('./landing-page/Cre8AppsPage'));
const AIVoiceAgent = lazyWithRetry(() => import('./landing-page/campaigns/AIVoiceAgent'));
// SEO solution subpages — real locale-prefixed URLs (/de|en/design/...),
// not reachable via the main nav, only from the expertise list + footer.
// UxDesignPage is the reference implementation of the shared
// SolutionPageTemplate — the master template for every Design/AI solution
// page. Every sibling below is a thin CONTENT config rendering that same
// template — together they cover the full section 06 expertise list.
const UxDesignPage = lazyWithRetry(() => import('./landing-page/seo/UxDesignPage'));
const ConsultingPage = lazyWithRetry(() => import('./landing-page/seo/ConsultingPage'));
const UiDesignPage = lazyWithRetry(() => import('./landing-page/seo/UiDesignPage'));
const IxDesignPage = lazyWithRetry(() => import('./landing-page/seo/IxDesignPage'));
const StrategyPage = lazyWithRetry(() => import('./landing-page/seo/StrategyPage'));
const PrototypingPage = lazyWithRetry(() => import('./landing-page/seo/PrototypingPage'));
const SeoPage = lazyWithRetry(() => import('./landing-page/seo/SeoPage'));
const DevelopmentPage = lazyWithRetry(() => import('./landing-page/seo/DevelopmentPage'));
const SocialAdsPage = lazyWithRetry(() => import('./landing-page/seo/SocialAdsPage'));
const TrainingsPage = lazyWithRetry(() => import('./landing-page/seo/TrainingsPage'));
// AI solution subpages — real locale-prefixed URLs (/de|en/ai/...),
// covering the full section 06 AI expertise list, same template as the
// Design pages above (SolutionPageTemplate reads category: 'ai' off each
// page's CONTENT to switch accent, tab and index code).
const AiConsultingPage = lazyWithRetry(() => import('./landing-page/seo/AiConsultingPage'));
const AiAgentsPage = lazyWithRetry(() => import('./landing-page/seo/AiAgentsPage'));
const DigitalConciergePage = lazyWithRetry(() => import('./landing-page/seo/DigitalConciergePage'));
const AiPipelinesPage = lazyWithRetry(() => import('./landing-page/seo/AiPipelinesPage'));
const AiContentPage = lazyWithRetry(() => import('./landing-page/seo/AiContentPage'));
const AiWorkshopsPage = lazyWithRetry(() => import('./landing-page/seo/AiWorkshopsPage'));
const AiFilmProductionPage = lazyWithRetry(() => import('./landing-page/seo/AiFilmProductionPage'));
const CustomAiDevelopmentPage = lazyWithRetry(() => import('./landing-page/seo/CustomAiDevelopmentPage'));
const AiVoiceCloningPage = lazyWithRetry(() => import('./landing-page/seo/AiVoiceCloningPage'));
const MinimalBarCaseStudy = lazyWithRetry(() => import('./landing-page/case-studies/MinimalBarCaseStudy'));

// Route tables live in src/seo/routes.js — the single source of truth shared
// with scripts/prerender.js, so the URLs the SPA resolves, the URLs that get a
// prerendered index.html and the URLs in the sitemap can never drift apart.
//
// The bare legacy aliases (/cre8guide, /concierge, /prototyping, /cre8apps) are
// 301'd to their /de/ equivalent by Apache in production; they are folded back
// in here so they still resolve client-side during `npm run dev`, where no
// Apache sits in front.
const MAIN_TAB_ROUTES = {
  ...BASE_MAIN_TAB_ROUTES,
  ...Object.fromEntries(
    Object.entries(LEGACY_ALIASES).map(([alias, target]) => [alias, BASE_MAIN_TAB_ROUTES[target]])
  ),
};

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger);

// Global ScrollTrigger config for mobile performance
if (typeof window !== 'undefined') {
  ScrollTrigger.config({ 
    ignoreMobileResize: true,
    limitCallbacks: true 
  });
}

const normalizeView = (view) => view;

export default function App() {
  const [view, setView] = useState(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const path = window.location.pathname.replace(/\/$/, ''); // Remove trailing slash
      if (SEO_SUBPAGE_ROUTES[path]) return SEO_SUBPAGE_ROUTES[path].view;
      if (MAIN_TAB_ROUTES[path]) return MAIN_TAB_ROUTES[path].view;
      return params.get('v') || 'home';
    }
    return 'home';
  });

  // Capture the tab implied by the ORIGINAL, real-navigation pathname once,
  // at the same instant as the view/lang initializers above — by the time
  // an effect runs, LandingPage's own URL-sync effect (a child, runs first)
  // may already have rewritten window.location.pathname to its default tab,
  // so re-reading location.pathname from an effect here would race it.
  const [initialMainTab] = useState(() => {
    if (typeof window === 'undefined') return null;
    const path = window.location.pathname.replace(/\/$/, '');
    return MAIN_TAB_ROUTES[path]?.tab || null;
  });

  // Sync initial tab based on path
  useEffect(() => {
    if (initialMainTab) {
      window.dispatchEvent(new CustomEvent('set_tab', { detail: initialMainTab }));
    }
  }, [initialMainTab]);

  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const t = params.get('theme');
      if (t === 'light' || t === 'dark') return t;
      // SEO subpages (e.g. /de/design/ux-design) are real <a href> links,
      // not client-side routes — a real browser navigation remounts the
      // app fresh, so without this fallback the theme silently resets to
      // light every time, ignoring whatever the user had set (same fix
      // `lang` below already has via 'cre8now-lang').
      const stored = localStorage.getItem('theme');
      if (stored === 'light' || stored === 'dark') return stored;
    }
    // First visit with no ?theme= and nothing stored: dark is the default.
    return 'dark';
  });

  const [lang, setLang] = useState(() => {
    if (typeof window !== 'undefined') {
      const path = window.location.pathname.replace(/\/$/, '');
      if (SEO_SUBPAGE_ROUTES[path]) return SEO_SUBPAGE_ROUTES[path].lang;
      const params = new URLSearchParams(window.location.search);
      const l = params.get('lang');
      // Bare legacy aliases (/cre8guide, /concierge, /cre8apps — kept alive
      // from before the lang-prefixed paths existed) default to 'de' but
      // must still honor an explicit ?lang=en on an old shared link.
      // Prefixed paths (/de|en/...) are unambiguous, so their own lang
      // always wins.
      if (MAIN_TAB_ROUTES[path]) {
        const isBareAlias = path === '/cre8guide' || path === '/concierge' || path === '/cre8apps';
        if (isBareAlias && (l === 'de' || l === 'en')) return l;
        return MAIN_TAB_ROUTES[path].lang;
      }
      if (l === 'de' || l === 'en') return l;
      return localStorage.getItem('cre8now-lang') || 'en';
    }
    return 'en';
  });

  const savedScrollY = useRef(0);

  const isLegal = view === 'impressum';

  const openLegal = (v) => {
    savedScrollY.current = window.scrollY;
    setView(v);
  };

  const closeLegal = () => {
    setView('home');
    requestAnimationFrame(() => window.scrollTo(0, savedScrollY.current));
  };

  const setViewWithLegal = (v) => {
    if (v === 'impressum') {
      openLegal(v);
    } else {
      setView(v);
    }
  };

  // Initialize Lenis smooth scroller — destroy when legal overlay is open
  useEffect(() => {
    // === Dynamic Canonical & Hreflang Tags ===
    const canonicalTag = document.getElementById('canonical-tag');
    const currentOrigin = "https://cre8now.design"; // Hardcoded canonical domain to avoid GSC domain mismatches (e.g. when accessing via alternate forwardings or IP)
    
    // Determine the base path based on current view/tab. Avoid index.html
    let path = window.location.pathname;
    if (path === '/index.html') {
      path = '/';
    }

    // SEO solution subpages (locale-prefixed real paths, e.g.
    // /de/design/ux-design) own their canonical/hreflang/title/lang —
    // set directly by the page component itself — so this generic,
    // query-param-based logic must not overwrite them.
    if (SEO_SUBPAGE_ROUTES[path.replace(/\/$/, '')]) {
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      if (isLegal || isMobile) {
        if (window.__lenis) { window.__lenis.destroy(); window.__lenis = null; }
        return;
      }
      const lenis = new Lenis({ duration: 1.2, easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)), orientation: 'vertical', smoothWheel: true });
      lenis.on('scroll', ScrollTrigger.update);
      gsap.ticker.add((time) => lenis.raf(time * 1000));
      gsap.ticker.lagSmoothing(0);
      window.__lenis = lenis;
      return () => { lenis.destroy(); window.__lenis = null; };
    }

    // Update HTML lang attribute. Trust the already-resolved `lang` state
    // instead of re-deriving it here — that state already handles the
    // ?lang= param, MAIN_TAB_ROUTES, and the localStorage/EN fallback for
    // a bare "/" (see its useState initializer above). Re-deriving it here
    // independently used to default a bare "/" to 'de', which disagreed
    // with the actually-rendered English content on first load.
    const mainTabRoute = MAIN_TAB_ROUTES[path.replace(/\/$/, '')];
    const htmlLang = mainTabRoute ? mainTabRoute.lang : lang;
    document.documentElement.lang = htmlLang;

    // The canonical tag is a fact about the URL, not about who is looking at
    // it, so nothing below reads the visitor's language. It used to be derived
    // from ?lang= so that "/" could canonicalize to "/?lang=en" — which made
    // the same URL declare a different canonical per visitor. Every branch now
    // builds the canonical from the path alone.
    // og:url has to track the canonical, or a shared link reports a different
    // URL than the one the page declares as its own. Only SolutionPageTemplate
    // used to do this, so the other 14 routes all announced the homepage.
    const setOgUrl = (href) => {
      const el = document.querySelector('meta[property="og:url"]');
      if (el) el.setAttribute('content', href);
    };

    const hreflangDe = document.querySelector('link[rel="alternate"][hreflang="de"]');
    const hreflangEn = document.querySelector('link[rel="alternate"][hreflang="en"]');
    const hreflangDefault = document.querySelector('link[rel="alternate"][hreflang="x-default"]');

    if (mainTabRoute?.tab) {
      // Design/AI/CRE8GUIDE tabs self-manage their own canonical/hreflang
      // (lang-prefixed, e.g. /de/design ↔ /en/design) from inside
      // LandingPage.jsx once activeTab settles — this generic, query-param
      // based logic must not overwrite them.
    } else if (mainTabRoute) {
      // CRE8APPS / Impressum — real lang-prefixed paths (/de|en/cre8apps,
      // /de|en/impressum), not query-param driven, so build canonical/
      // hreflang directly from the matched route's slug instead of the
      // generic ?lang= logic below.
      const { slug } = mainTabRoute;
      if (canonicalTag) canonicalTag.setAttribute('href', currentOrigin + path);
      setOgUrl(currentOrigin + path);
      // These routes never pass through LandingPage's tab block, so their
      // og:locale would otherwise keep index.html's English default on the
      // German pages.
      const ogLocale = document.querySelector('meta[property="og:locale"]');
      if (ogLocale) ogLocale.setAttribute('content', mainTabRoute.lang === 'de' ? 'de_DE' : 'en_US');
      if (hreflangDe) hreflangDe.setAttribute('href', `${currentOrigin}/de/${slug}`);
      if (hreflangEn) hreflangEn.setAttribute('href', `${currentOrigin}/en/${slug}`);
      if (hreflangDefault) hreflangDefault.setAttribute('href', `${currentOrigin}/de/${slug}`);
    } else {
      // The homepage. It serves whichever language the visitor's browser or
      // stored preference implies, so it is the x-default of a cluster whose
      // localized members are the real, pinned-language URLs /de/design and
      // /en/design — see HOME_HREFLANG in src/seo/routes.js.
      //
      // It deliberately does NOT canonicalize to "/?lang=en" for English
      // visitors any more. That old query-param scheme made "/" declare a
      // different canonical per visitor and put a URL into the hreflang set
      // that is not a crawlable page of its own — both reasons Search Console
      // collapsed everything down to the bare homepage. The canonical here is
      // always self-referencing and language-independent.
      if (canonicalTag) canonicalTag.setAttribute('href', currentOrigin + '/');
      setOgUrl(currentOrigin + '/');
      if (hreflangDe) hreflangDe.setAttribute('href', currentOrigin + HOME_HREFLANG.de);
      if (hreflangEn) hreflangEn.setAttribute('href', currentOrigin + HOME_HREFLANG.en);
      if (hreflangDefault) hreflangDefault.setAttribute('href', currentOrigin + HOME_HREFLANG['x-default']);
    }

    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

    if (isLegal || isMobile) {
      if (window.__lenis) {
        window.__lenis.destroy();
        window.__lenis = null;
      }
      return;
    }

    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: 'vertical',
      smoothWheel: true,
    });

    // Connect Lenis to GSAP ScrollTrigger
    lenis.on('scroll', ScrollTrigger.update);
    gsap.ticker.add((time) => lenis.raf(time * 1000));
    gsap.ticker.lagSmoothing(0);

    // Expose globally so other components can use it
    window.__lenis = lenis;

    return () => {
      lenis.destroy();
      window.__lenis = null;
    };
  }, [isLegal]);

  // Sync view and lang state with URL
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const path = window.location.pathname.replace(/\/$/, '');
      // SEO solution subpages manage their own title/URL entirely — never
      // rewrite their clean locale-prefixed path with ?v=/&lang= params.
      if (SEO_SUBPAGE_ROUTES[path]) return;
      // Same for every MAIN_TAB_ROUTES path — if the address bar already
      // shows the CANONICAL lang-prefixed path for the current `view`,
      // there's nothing to sync: tab routes (design/ai/cre8guide/concierge)
      // are fully owned by LandingPage.jsx's own URL-sync effect (including
      // ?theme=); Impressum just needs its ?theme= kept in sync in place.
      // Bare legacy aliases (/cre8guide, /concierge, /cre8apps — no /de|en/
      // prefix) must NOT take this shortcut even when their `view` already
      // matches, or they'd never redirect to their canonical prefixed URL.
      // If `view` is legitimately transitioning away from/into one of these
      // (e.g. home -> impressum) while the address bar doesn't match yet,
      // let this effect proceed so it can redirect properly.
      const mainTabRoute = MAIN_TAB_ROUTES[path];
      const isCanonicalMainTabPath = mainTabRoute && (path.startsWith('/de/') || path.startsWith('/en/'));
      if (isCanonicalMainTabPath && mainTabRoute.view === view) {
        if (!mainTabRoute.tab) {
          const legalUrl = new URL(window.location.href);
          legalUrl.searchParams.set('theme', theme);
          window.history.replaceState({}, '', legalUrl.toString());
        }
        return;
      }
      const url = new URL(window.location.href);
      const currentV = isCanonicalMainTabPath
        ? mainTabRoute.view
        : normalizeView(url.searchParams.get('v') || 'home');
      
      // Update lang and theme in URL
      url.searchParams.set('lang', lang);
      url.searchParams.set('theme', theme);
      
      // Remove tab parameter if not on home view
      if (view !== 'home') {
        url.searchParams.delete('tab');
      }
      
      localStorage.setItem('cre8now-lang', lang);

      if (currentV !== view) {
        if (view === 'home') {
          // If we are coming from a nested path like /cre8guide, reset pathname
          const cleanUrl = new URL(window.location.origin);
          cleanUrl.searchParams.set('lang', lang);
          cleanUrl.searchParams.set('theme', theme);
          // Preserve ?tab if it exists
          const tab = url.searchParams.get('tab');
          if (tab) cleanUrl.searchParams.set('tab', tab);
          
          document.title = 'CRE8NOW | AI & Design Lab';
          window.history.pushState({ view, theme }, '', cleanUrl.toString());
          return;
        } else if (view === 'cre8apps') {
          const appsUrl = new URL(window.location.origin + (lang === 'de' ? '/de/cre8apps' : '/en/cre8apps'));
          appsUrl.searchParams.set('theme', theme);

          document.title = 'CRE8APPS | CRE8NOW';
          window.history.pushState({ view, theme }, '', appsUrl.toString());
          return;
        } else if (view === 'campaign-ai-voice-agent') {
          const conciergeUrl = new URL(window.location.origin + (lang === 'de' ? '/de/concierge' : '/en/concierge'));
          conciergeUrl.searchParams.set('theme', theme);

          document.title = 'Digital Concierge | CRE8NOW Accelerator';
          window.history.pushState({ view, theme }, '', conciergeUrl.toString());
          return;
        } else if (view === 'impressum') {
          const legalUrl = new URL(window.location.origin + (lang === 'de' ? '/de/impressum' : '/en/impressum'));
          legalUrl.searchParams.set('theme', theme);

          document.title = 'CRE8NOW | Impressum';
          window.history.pushState({ view, theme }, '', legalUrl.toString());
          return;
        } else {
          // CLEAN DOMAIN FOR CAMPAIGNS: Remove landing page noise
          const cleanUrl = new URL(window.location.origin + window.location.pathname);
          cleanUrl.searchParams.set('v', view);
          cleanUrl.searchParams.set('lang', lang);
          cleanUrl.searchParams.set('theme', theme);

          const campaignTitles = {
            'campaign-ai-voice-agent': 'Digital Concierge'
          };
          const campaignName = campaignTitles[view] || view.replace('campaign-', '').split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
          document.title = `${campaignName} | CRE8NOW Accelerator`;
          
          window.history.pushState({ view, theme }, '', cleanUrl.toString());
          return; // Avoid the default pushState below
        }
      }
      window.history.pushState({ view, theme }, '', url.toString());
    }
  }, [view, lang, theme]);

  // Title + description for CRE8APPS and Impressum.
  //
  // The navigation effect above only sets document.title while transitioning
  // between views, so a direct load of /de/impressum never ran it — the page
  // kept whatever LandingPage (rendering behind it) had put there, which made
  // all four of these URLs ship a wrong or duplicated title. This effect keys
  // off the resolved view instead, so it is correct on first paint, on
  // client-side navigation and in the prerendered HTML alike.
  useEffect(() => {
    const meta = PAGE_META[view]?.[lang] ?? PAGE_META[view]?.de;
    if (!meta) return;
    document.title = meta.title;
    const apply = (selector, attr) => {
      const el = document.querySelector(selector);
      if (el) el.setAttribute(attr, meta.description);
    };
    apply('meta[name="description"]', 'content');
    apply('meta[property="og:description"]', 'content');
    apply('meta[name="twitter:description"]', 'content');
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute('content', meta.title);
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) twitterTitle.setAttribute('content', meta.title);
  }, [view, lang]);

  // Handle back/forward buttons
  useEffect(() => {
    const handlePopState = (event) => {
      const path = window.location.pathname.replace(/\/$/, '');
      const mainTabRoute = MAIN_TAB_ROUTES[path];
      if (mainTabRoute) {
        setView(mainTabRoute.view);
        setLang(mainTabRoute.lang);
        if (mainTabRoute.tab) {
          window.dispatchEvent(new CustomEvent('set_tab', { detail: mainTabRoute.tab }));
        }
      } else if (event.state && event.state.view) {
        setView(event.state.view);
      } else {
        const params = new URLSearchParams(window.location.search);
        setView(params.get('v') || 'home');
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    const root = window.document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    localStorage.setItem('theme', theme);
  }, [theme]);

  useEffect(() => {
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

    if (!isMobile) {
      gsap.ticker.lagSmoothing(0);
    }

    // Refresh ScrollTrigger and reset
    ScrollTrigger.refresh();
    if (view !== 'impressum' && view !== 'home') {
      if (window.__lenis) {
        window.__lenis.scrollTo(0, { immediate: true });
      } else {
        window.scrollTo(0, 0);
      }
    }

    return () => {
      // Clean up listeners
    };
  }, [view]);

  return (
    <div className="min-h-screen text-color font-sans selection:bg-design/20 transition-colors duration-1000 bg-bg-color">
      <CustomCursor />
      
      {/* The boundary sits OUTSIDE Suspense on purpose: a rejected dynamic
          import throws past the fallback, and with nothing to catch it React
          unmounted the whole root — the blank page visitors reported as "it
          just goes white". */}
      <RouteErrorBoundary lang={lang}>
      <Suspense
        fallback={
          /* Deliberately just the backdrop — no logo, no label.
             Whatever this covers for is momentary (the route chunk is
             modulepreloaded), and every page behind it opens on SplashScreen,
             whose logo fades up from opacity 0. Putting a mark here too meant
             the logo appeared instantly and then faded in a second time, which
             is the same double-entrance this boot sequence exists to remove.
             The splash owns the logo; this only holds the colour. */
          <div className="fixed inset-0 bg-bg-color z-[9999]" role="status" aria-label="CRE8NOW" />
        }
      >
        {(view === 'home' || view === 'impressum' || view === 'campaign-ai-voice-agent') && (
          <LandingPage setView={setViewWithLegal} theme={theme} setTheme={setTheme} lang={lang} setLang={setLang} initialTab={view === 'campaign-ai-voice-agent' ? 'concierge' : undefined} />
        )}

        <AnimatePresence>
          {view === 'impressum' && (
            <motion.div
              key={view}
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ duration: 0.8, ease: [0.83, 0, 0.17, 1] }}
              className="fixed inset-0 z-[200] w-screen h-dvh bg-bg-color"
            >
              <div className="w-full h-full overflow-y-auto overscroll-contain">
                <Impressum onBack={closeLegal} />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {view === 'cre8apps' && (
          <Cre8AppsPage setView={setView} theme={theme} setTheme={setTheme} lang={lang} setLang={setLang} />
        )}

        {view === 'ux-design' && (
          <UxDesignPage setView={setView} lang={lang} />
        )}

        {view === 'consulting' && (
          <ConsultingPage setView={setView} lang={lang} />
        )}

        {view === 'ui-design' && (
          <UiDesignPage setView={setView} lang={lang} />
        )}

        {view === 'ix-design' && (
          <IxDesignPage setView={setView} lang={lang} />
        )}

        {view === 'strategy' && (
          <StrategyPage setView={setView} lang={lang} />
        )}

        {view === 'prototyping' && (
          <PrototypingPage setView={setView} lang={lang} />
        )}

        {view === 'seo' && (
          <SeoPage setView={setView} lang={lang} />
        )}

        {view === 'development' && (
          <DevelopmentPage setView={setView} lang={lang} />
        )}

        {view === 'social-ads' && (
          <SocialAdsPage setView={setView} lang={lang} />
        )}

        {view === 'trainings' && (
          <TrainingsPage setView={setView} lang={lang} />
        )}

        {view === 'ai-consulting' && (
          <AiConsultingPage setView={setView} lang={lang} />
        )}

        {view === 'ai-agents' && (
          <AiAgentsPage setView={setView} lang={lang} />
        )}

        {view === 'digital-concierge' && (
          <DigitalConciergePage setView={setView} lang={lang} />
        )}

        {view === 'ai-pipelines' && (
          <AiPipelinesPage setView={setView} lang={lang} />
        )}

        {view === 'ai-content' && (
          <AiContentPage setView={setView} lang={lang} />
        )}

        {view === 'ai-workshops' && (
          <AiWorkshopsPage setView={setView} lang={lang} />
        )}

        {view === 'ai-film-production' && (
          <AiFilmProductionPage setView={setView} lang={lang} />
        )}

        {view === 'custom-ai-development' && (
          <CustomAiDevelopmentPage setView={setView} lang={lang} />
        )}

        {view === 'ai-voice-cloning' && (
          <AiVoiceCloningPage setView={setView} lang={lang} />
        )}

        {view === 'case-minimal-bar' && (
          <MinimalBarCaseStudy lang={lang} />
        )}
      </Suspense>
      </RouteErrorBoundary>

      <CartOverlay 
        lang={lang}
        setView={setView}
        activeColor={{
        'cre8apps': '#F41465',
        'campaign-ai-voice-agent': '#ec4899'
      }[view]} />

      <CookieBanner lang={lang} theme={theme} />

    </div>
  );
}


